import React, { useEffect, useMemo, useState } from 'react';
import { Search, Film, Plus, Link as LinkIcon, Upload as UploadIcon, Video as VideoIcon } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import VideoCard from '../components/VideoCard';
import VideoPlayer from '../components/VideoPlayer';
import VideoRecorder from '../components/VideoRecorder';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';
import { PlayersAPI, VideosAPI } from '../api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoLibrary() {
  const [q, setQ] = useState('');
  const [playerFilter, setPlayerFilter] = useState('all');
  const [active, setActive] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [tab, setTab] = useState('url');

  // Common form fields
  const [playerId, setPlayerId] = useState('');
  const [title, setTitle] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [recordedFile, setRecordedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [players, setPlayers] = useState([]);
  const [videos, setVideos] = useState([]);
  const { toast } = useToast();

  const load = () => {
    PlayersAPI.list().then(setPlayers);
    VideosAPI.list().then(setVideos);
  };
  useEffect(() => { load(); }, []);

  const activePlayer = useMemo(
    () => (active ? players.find(p => p.id === active.playerId) : null),
    [active, players],
  );

  const list = useMemo(() => videos.filter(v => {
    const matchQ = !q || v.title.toLowerCase().includes(q.toLowerCase()) || v.tags?.some(t => t.toLowerCase().includes(q.toLowerCase()));
    const matchP = playerFilter === 'all' || v.playerId === playerFilter;
    return matchQ && matchP;
  }), [q, playerFilter, videos]);

  const resetForm = () => {
    setPlayerId('');
    setTitle('');
    setYoutubeUrl('');
    setUploadFile(null);
    setRecordedFile(null);
    setTab('url');
  };

  const uploadMultipart = async (file, source) => {
    const form = new FormData();
    form.append('playerId', playerId);
    form.append('title', title);
    form.append('source', source);
    form.append('file', file);
    await axios.post(`${BACKEND_URL}/api/videos/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const submit = async () => {
    if (!playerId || !title) {
      toast({ title: 'Missing details', description: 'Player and title are required.' });
      return;
    }
    setSubmitting(true);
    try {
      if (tab === 'url') {
        if (!youtubeUrl) { toast({ title: 'Add a YouTube URL' }); setSubmitting(false); return; }
        await VideosAPI.create({ playerId, title, youtubeUrl });
      } else if (tab === 'upload') {
        if (!uploadFile) { toast({ title: 'Pick a file' }); setSubmitting(false); return; }
        await uploadMultipart(uploadFile, 'upload');
      } else if (tab === 'record') {
        if (!recordedFile) { toast({ title: 'Record a clip first' }); setSubmitting(false); return; }
        await uploadMultipart(recordedFile, 'recording');
      }
      toast({ title: 'Clip added', description: title });
      setAddOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast({ title: 'Failed to add clip', description: e?.response?.data?.detail || e?.message || 'Try again.' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Video Library"
        subtitle={`${list.length} clip${list.length !== 1 ? 's' : ''}`}
        right={
          <Button onClick={() => setAddOpen(true)} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Clip
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input placeholder="Search clips or tags..." value={q} onChange={e => setQ(e.target.value)}
            className="pl-9 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400" />
        </div>
        <Select value={playerFilter} onValueChange={setPlayerFilter}>
          <SelectTrigger className="w-[220px] bg-white border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-900">
            <SelectItem value="all">All Players</SelectItem>
            {players.map(p => (<SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  #{p.jerseyNumber}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {active && (
        <div className="mb-6">
          <VideoPlayer video={active} playerId={active.playerId}
            playerPosition={activePlayer?.position} />
          <div className="mt-2 text-right">
            <Button variant="ghost" onClick={() => setActive(null)} className="text-slate-500 hover:text-slate-900">Close player</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(v => (
          <VideoCard key={v.id} video={v} onPlay={() => setActive(v)}
            showPlayer player={players.find(p => p.id === v.playerId)} />
        ))}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16">
            <Film className="h-10 w-10 text-slate-700 mx-auto mb-2" />
            <div className="text-slate-500">No clips match your filters.</div>
          </div>
        )}
      </div>

      {/* Add clip dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Video Clip</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Paste a YouTube URL, upload a file from your device, or record live using your camera.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-600">Player</Label>
                <Select value={playerId} onValueChange={setPlayerId}>
                  <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900">
                    <SelectValue placeholder="Select player" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-900">
                    {players.map(p => (<SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  {p.position}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Clip title</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Power Play Highlights"
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400" />
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-50 border border-slate-200 grid grid-cols-3">
                <TabsTrigger value="url" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">
                  <LinkIcon className="h-3.5 w-3.5 mr-1.5" /> YouTube URL
                </TabsTrigger>
                <TabsTrigger value="upload" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">
                  <UploadIcon className="h-3.5 w-3.5 mr-1.5" /> Upload file
                </TabsTrigger>
                <TabsTrigger value="record" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">
                  <VideoIcon className="h-3.5 w-3.5 mr-1.5" /> Record live
                </TabsTrigger>
              </TabsList>

              <TabsContent value="url" className="mt-4">
                <Label className="text-sm text-slate-600">YouTube URL</Label>
                <Input value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400" />
                <p className="text-xs text-slate-500 mt-2">Paste any YouTube link. We'll extract the video ID automatically.</p>
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <label className="block cursor-pointer border-2 border-dashed border-slate-300 hover:border-sky-500/40 rounded-xl p-6 text-center transition-colors">
                  <input type="file" accept="video/*" className="hidden"
                    onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                  <UploadIcon className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                  {uploadFile ? (
                    <>
                      <div className="font-medium text-slate-900">{uploadFile.name}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {(uploadFile.size / (1024 * 1024)).toFixed(1)} MB  click to change
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-slate-600">Click to choose a video file</div>
                      <div className="text-xs text-slate-500 mt-1">MP4, MOV, WebM  from your phone, camera or computer</div>
                    </>
                  )}
                </label>
              </TabsContent>

              <TabsContent value="record" className="mt-4">
                <VideoRecorder
                  onRecorded={(file) => setRecordedFile(file)}
                  onClear={() => setRecordedFile(null)}
                />
                <p className="text-xs text-slate-500 mt-2">Recording uses your device camera and mic. Works best on a phone or tablet at the rink.</p>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}
              className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">Cancel</Button>
            <Button onClick={submit} disabled={submitting}
              className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
              {submitting ? 'Saving...' : 'Add clip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
